import React, { useEffect, useState, useMemo } from 'react';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Gift,
  Users,
  Sparkles,
  Copy,
  Share2,
  MessageCircle,
  CheckCircle2,
  Trophy,
  Flame,
} from 'lucide-react';

export default function ReferralPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const load = () =>
    api.get('/referral/me').then(({ data }) => {
      setData(data);
      setLoading(false);
    });

  useEffect(() => {
    load().catch(() => {
      toast.error('Failed to load referral info');
      setLoading(false);
    });
  }, []);

  const shareLink = useMemo(() => {
    if (!data?.referral_code) return '';
    return `${window.location.origin}/register?ref=${data.referral_code}`;
  }, [data]);

  const shareText = useMemo(() => {
    if (!data?.referral_code) return '';
    return (
      'I’m using StudyTrack AI for chapter-wise mocks (Boards · JEE · NEET). ' +
      'Sign up with my code and we BOTH get a bonus chapter unlocked. \n\n' +
      `Code: ${data.referral_code}\n${shareLink}`
    );
  }, [data, shareLink]);

  const copyCode = () => {
    if (!data?.referral_code) return;
    navigator.clipboard.writeText(data.referral_code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 1600);
    toast.success('Code copied');
  };

  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1600);
    toast.success('Link copied');
  };

  const webShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'StudyTrack AI', text: shareText, url: shareLink });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  if (loading || !data) {
    return (
      <AppShell>
        <div className="grid min-h-[50vh] place-items-center text-muted-foreground">Loading…</div>
      </AppShell>
    );
  }

  const credits = data.bonus_credits || 0;
  const referrals = data.referrals_count || 0;

  return (
    <AppShell>
      <div className="space-y-6" data-testid="referral-page">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-black text-white">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(600px circle at 10% 0%, rgba(220,38,38,0.32), transparent 55%), radial-gradient(500px circle at 100% 100%, rgba(239,68,68,0.14), transparent 60%)',
            }}
          />
          <div className="relative px-5 py-8 sm:px-10 sm:py-10">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
              <Flame className="h-3.5 w-3.5" />
              Refer & Earn
            </div>
            <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl">
              Share the grind.{' '}
              <span className="bg-gradient-to-b from-red-300 to-red-600 bg-clip-text text-transparent">
                Both of you win.
              </span>
            </h1>
            <p className="mt-2 max-w-2xl text-white/70">
              Send friends your code. When they buy any plan, you AND they each get one bonus chapter unlocked — usable on any section.
            </p>
            <div className="mt-6 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
              <MiniStat kpi={referrals} label="Friends referred" icon={Users} />
              <MiniStat kpi={credits} label="Bonus credits" icon={Gift} />
              <MiniStat kpi={referrals} label="Lifetime unlocks earned" icon={Trophy} />
            </div>
          </div>
        </section>

        {/* Code + share */}
        <section className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="space-y-3 p-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Your referral code</div>
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono-ui text-3xl font-bold tracking-wider" data-testid="referral-code">
                  {data.referral_code}
                </div>
                <Button variant="outline" onClick={copyCode} className="gap-1.5" data-testid="copy-referral-code">
                  {copiedCode ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copiedCode ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div className="text-sm text-muted-foreground">
                Friends enter this at sign-up. They can also use the link below — it pre-fills the code.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Your referral link</div>
              <div className="flex items-center gap-2">
                <Input readOnly value={shareLink} className="h-11 font-mono-ui text-xs" data-testid="referral-link" />
                <Button variant="outline" onClick={copyLink} className="gap-1.5 shrink-0" data-testid="copy-referral-link">
                  {copiedLink ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle className="h-4 w-4" />
                  Share on WhatsApp
                </a>
                <Button onClick={webShare} className="gap-1.5" data-testid="share-any-app">
                  <Share2 className="h-4 w-4" />
                  Share…
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* How it works */}
        <section>
          <Card>
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-red-500" />
                <div className="font-display text-lg font-semibold">How it works</div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {[
                  {
                    step: '1',
                    title: 'Share your code',
                    desc: 'Send your code or link to friends preparing for the same exam.',
                  },
                  {
                    step: '2',
                    title: 'They buy any plan',
                    desc: 'When your friend completes their first paid purchase, we notice.',
                  },
                  {
                    step: '3',
                    title: 'Both get +1 credit',
                    desc: 'You AND your friend each get 1 chapter unlock — redeem on any chapter, any section.',
                  },
                ].map((s) => (
                  <div key={s.step} className="relative overflow-hidden rounded-lg border p-4">
                    <div className="absolute -right-3 -top-3 font-display text-5xl font-bold text-muted-foreground/10">
                      {s.step}
                    </div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-red-500">Step {s.step}</div>
                    <div className="font-display text-lg font-semibold">{s.title}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{s.desc}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs text-muted-foreground">
                Rules: bonus fires once per referred friend, on their first successful paid order. Credits never expire and can be redeemed on any chapter from the Chapter Hub.
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Credits summary */}
        {credits > 0 && (
          <section>
            <Card className="border-red-500/30 bg-red-500/5">
              <CardContent className="flex flex-col items-start justify-between gap-3 p-6 sm:flex-row sm:items-center">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_24px_rgba(239,68,68,0.35)]">
                    <Gift className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-semibold">You have {credits} chapter{credits > 1 ? 's' : ''} to redeem</div>
                    <div className="text-sm text-muted-foreground">
                      Head to any chapter’s hub and hit “Redeem referral credit”.
                    </div>
                  </div>
                </div>
                <Badge className="bg-red-600 text-white hover:bg-red-500">Ready to use</Badge>
              </CardContent>
            </Card>
          </section>
        )}

        {data.referred_by && (
          <div className="text-xs text-muted-foreground">Referred by code: <span className="font-mono-ui">{data.referred_by}</span></div>
        )}
      </div>
    </AppShell>
  );
}

function MiniStat({ kpi, label, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-white/60">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {label}
      </div>
      <div className="font-display text-2xl font-bold">
        <span className="bg-gradient-to-b from-red-300 to-red-500 bg-clip-text text-transparent">{kpi}</span>
      </div>
    </div>
  );
}
